import React, { useEffect, useState, useRef } from "react"
import "./ViewSchool.scss"
import SchoolApi from "Api/SchoolApi"
import { useNavigate, useParams } from "react-router-dom"
import { Button, Card, CardBody, Col, Row, Spinner, Table } from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { Toast } from "primereact/toast"

const ViewSchool = () => {
    document.title = "View School"
    const [pageLoading, setPageLoading] = useState(true)
    const toast = useRef(null)
    const navigate = useNavigate("")
    const { schoolCode } = useParams()
    const accessToken = localStorage.getItem("accessToken")

    const [schoolData, setSchoolData] = useState({})

    useEffect(() => {
        if (schoolCode) {
            getSchoolDetails()
        }
    }, [schoolCode])

    const getSchoolDetails = async () => {
        setPageLoading(true)
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
            const response = await SchoolApi.getSchoolById(schoolCode, config)
            if (response.data.status) {
                setSchoolData(response.data.school)
            }
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch school details",
                life: 3000,
            })
        } finally {
            setPageLoading(false)
        }
    }

    const breadcrumbItems = [
        { label: "Pixwik", link: "/" },
        { label: "Schools", link: "/schoolList" },
        { label: "View School", link: "#" },
    ]

    const handleBreadcrumbClick = link => {
        navigate(link)
    }

    if (pageLoading) {
        return (
            <div className="text-center p-5">
                <Spinner />
            </div>
        )
    }

    return (
        <div className="view-school">
            <Toast ref={toast} />
            <Row>
                <Col xs="12">
                    <div className="school-list-header d-flex align-items-center justify-content-between">
                        <CustomBreadCrumb
                            data={breadcrumbItems}
                            pageTitle="View School"
                            handleBreadcrumbClick={handleBreadcrumbClick}
                        />
                        <div className="d-flex gap-2">
                            <Button
                                color="primary"
                                onClick={() => navigate(`/edit-school/${schoolCode}`)}
                            >
                                <i className="mdi mdi-pencil me-1" />
                                Edit
                            </Button>
                            <Button color="secondary" onClick={() => navigate("/schoolList")}>
                                Back to List
                            </Button>
                        </div>
                    </div>

                    <Card className="mt-4">
                        <CardBody>
                            <h5 className="mb-4">School Details</h5>
                            <Table bordered>
                                <tbody>
                                    <tr>
                                        <th width="30%">School Name</th>
                                        <td>{schoolData.schoolName || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Display Name</th>
                                        <td>{schoolData.displayName || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Type</th>
                                        <td>
                                            {Array.isArray(schoolData.type)
                                                ? schoolData.type.join(", ")
                                                : schoolData.type || "N/A"}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Medium of Instruction</th>
                                        <td>{schoolData.mediumOfInstruction || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Year Established</th>
                                        <td>{schoolData.yearEstablished || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Country</th>
                                        <td>{schoolData.country || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>State</th>
                                        <td>{schoolData.state || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>City</th>
                                        <td>{schoolData.city || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Pin Code</th>
                                        <td>{schoolData.pinCode || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Address</th>
                                        <td>{schoolData.address || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Email</th>
                                        <td>{schoolData.email || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Contact Number</th>
                                        <td>{schoolData.contactNumber || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Official Website</th>
                                        <td>
                                            {schoolData.officialWebsite ? (
                                                <a
                                                    href={schoolData.officialWebsite}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {schoolData.officialWebsite}
                                                </a>
                                            ) : (
                                                "N/A"
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Affiliation Board</th>
                                        <td>
                                            {Array.isArray(schoolData.affiliationBoard)
                                                ? schoolData.affiliationBoard.join(", ")
                                                : schoolData.affiliationBoard || "N/A"}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>About Content</th>
                                        <td>
                                            <div style={{ whiteSpace: "pre-wrap" }}>
                                                {schoolData.aboutContent || "N/A"}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Highlights</th>
                                        <td>
                                            <div style={{ whiteSpace: "pre-wrap" }}>
                                                {schoolData.highlights || "N/A"}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Campus Size</th>
                                        <td>{schoolData.campusSize || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Number of Students</th>
                                        <td>{schoolData.noOfStudents || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Number of Teachers</th>
                                        <td>{schoolData.noOfTeachers || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Grades Offered</th>
                                        <td>
                                            {Array.isArray(schoolData.gradesOffered)
                                                ? schoolData.gradesOffered.join(", ")
                                                : schoolData.gradesOffered || "N/A"}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Number of Labs</th>
                                        <td>{schoolData.noOfLabs || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Sports Available</th>
                                        <td>
                                            {schoolData.sportsAvailable ? (
                                                <span className="badge bg-success">Available</span>
                                            ) : (
                                                <span className="badge bg-secondary">Not Available</span>
                                            )}
                                        </td>
                                    </tr>
                                    {schoolData.sportsAvailable && (
                                        <tr>
                                            <th>Sports Facilities</th>
                                            <td>{schoolData.sportsFacilities || "N/A"}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <th>Transport Facilities</th>
                                        <td>
                                            {schoolData.transportFacilities ? (
                                                <span className="badge bg-success">Available</span>
                                            ) : (
                                                <span className="badge bg-secondary">Not Available</span>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Hostel Facility</th>
                                        <td>
                                            {schoolData.hostelFacility ? (
                                                <span className="badge bg-success">Available</span>
                                            ) : (
                                                <span className="badge bg-secondary">Not Available</span>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Yearly Intake</th>
                                        <td>{schoolData.yearlyIntake || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th>Verified Status</th>
                                        <td>
                                            {schoolData.verifiedStatus === "approved" && (
                                                <span className="badge bg-success">Approved</span>
                                            )}
                                            {schoolData.verifiedStatus === "rejected" && (
                                                <span className="badge bg-danger">Rejected</span>
                                            )}
                                            {schoolData.verifiedStatus === "pending" && (
                                                <span className="badge bg-warning">Pending</span>
                                            )}
                                            {!schoolData.verifiedStatus && "N/A"}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}

export default ViewSchool
